/**
 * Context API Client
 * 處理 LINE 群組與 Tenant 綁定相關的 API 調用
 * 依領域拆成 liteAuthClient、liteGroupSettingsClient，此檔統一 re-export 並保留解除綁定／刪除商家／預繳名單
 */

export {
  resolveContext,
  getUserTenants,
  bindGroup,
  getUserByLineIdToken,
  registerNewUser,
  registerNewUserWithTenant,
  createTenant,
  checkGroupTenant,
  checkGroupStatus,
  bindGroupWithSquare,
  type CheckGroupTenantActivityPublishConfig,
  type CheckGroupTenantUserReplyFormat,
} from './liteAuthClient.js';

export {
  updateLiteGroupSettings,
  getSquareSettings,
  updateLiteSquareSettings,
  uploadLiteFlexCover,
  type LiteGroupSettingsData,
} from './liteGroupSettingsClient.js';

import { getApiBase } from './apiUtils.js';
import { handleLiteErrorResponse } from './liteApiError.js';

/**
 * 解除 Lite 群組綁定（群組擁有者將此 LINE 群組與助理解綁）
 */
export async function unbindLiteGroup(
  groupId: string,
  jwtToken: string
): Promise<void> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');
  const url = `${apiBase}/api/lite/auth/unbind-group?groupId=${encodeURIComponent(groupId)}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwtToken}`,
    },
    body: JSON.stringify({ groupId }),
  });

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '解除綁定失敗，請稍後再試',
    });
  }
}

/**
 * 刪除 Lite 商家（設為停用）
 */
export async function deleteLiteShop(
  params: { groupId?: string; suffix?: string },
  jwtToken: string
): Promise<void> {
  const apiBase = getApiBase();
  const search = new URLSearchParams();
  if (params.groupId?.trim()) search.set('groupId', params.groupId.trim());
  if (params.suffix?.trim())
    search.set('suffix', params.suffix.trim().toUpperCase());
  if (search.toString() === '') {
    throw new Error('請提供 groupId 或 suffix');
  }
  const { apiFetch } = await import('./apiUtils.js');
  const url = `${apiBase}/api/lite/auth/delete-shop?${search.toString()}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwtToken}`,
    },
  });

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '刪除商家失敗，請稍後再試',
    });
  }
}

/** 預繳名單單筆 */
export interface PrepaidListItem {
  userId: string;
  name: string;
  remainingCount: number;
  gender: 'male' | 'female' | 'other' | null;
  updatedAt: string;
}

/**
 * 取得當前商家的預繳名單（名稱、次數、性別、最近更新）
 */
export async function getPrepaidList(
  jwtToken: string,
  options?: { squareSuffix?: string }
): Promise<PrepaidListItem[]> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');
  const response = await liteApiFetch(
    `${apiBase}/api/lite/prepaid-list`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '取得預繳名單失敗，請稍後再試',
    });
  }

  const json = (await response.json()) as {
    success: boolean;
    data: PrepaidListItem[];
  };
  return json.data ?? [];
}
